import type {
  TrackingCheckoutSchema,
  TrackingProductsSchema,
} from '@/schemas/tracking.product'
import type { z } from 'zod'

declare const fbq:
  | ((param1: string, param2: string, param3: unknown) => void)
  | undefined

interface FacebookPixelCartTrackProps {
  content_ids: string
  content_name: string
  content_type?: string
  contents: Array<{ id: string; quantity: number }>
  currency: 'BRL'
  value: number
}

interface FacebookPixelCheckoutTrackProps {
  content_type: string
  content_category: string[]
  content_ids: string[]
  contents: Array<{ id: string; quantity: number }>
  currency: 'BRL'
  num_items: number
  value: number
}

export const buildCheckoutTrack = (
  properties: z.infer<typeof TrackingCheckoutSchema>,
): FacebookPixelCheckoutTrackProps => {
  const products = properties.Produtos ?? []

  return {
    content_type:
      products && products?.length > 1 ? 'product_group' : 'product',
    content_category:
      products && products?.length > 0
        ? products.map((item) => item.Tipo || 'Curso')
        : ['Curso'],
    content_ids: products.map((item) => item.ProdutoID),
    contents: products.map((item) => {
      return { id: item.ProdutoID, quantity: 1 }
    }),
    currency: 'BRL',
    num_items: products.length,
    value: properties.Total,
  }
}

export const buildCartTrack = (
  properties: z.infer<typeof TrackingProductsSchema>,
): FacebookPixelCartTrackProps => {
  return {
    content_ids: properties.ProdutoID,
    content_name: properties.Produto,
    content_type: properties.Tipo,
    contents: [{ id: properties.ProdutoID, quantity: 1 }],
    currency: 'BRL',
    value: properties.Valor,
  }
}

export const trackAddToCart = (
  properties: z.infer<typeof TrackingProductsSchema>,
): void => {
  if (typeof fbq !== 'undefined') {
    const trackData = buildCartTrack(properties)
    fbq('track', 'AddToCart', trackData)
  }
}

export const trackInitiateCheckout = (
  properties: z.infer<typeof TrackingCheckoutSchema>,
): void => {
  if (typeof fbq !== 'undefined') {
    const trackData = buildCheckoutTrack(properties)
    fbq('track', 'InitiateCheckout', trackData)
  }
}

export const trackAddPaymentInfo = (
  properties: z.infer<typeof TrackingCheckoutSchema>,
): void => {
  if (typeof fbq !== 'undefined') {
    const trackData = buildCheckoutTrack(properties)
    fbq('track', 'AddPaymentInfo', trackData)
  }
}

export const trackPurchase = (
  properties: z.infer<typeof TrackingCheckoutSchema>,
): void => {
  if (typeof fbq !== 'undefined') {
    const trackData = buildCheckoutTrack(properties)
    fbq('track', 'Purchase', trackData)
  }
}

export default {
  trackAddToCart,
  trackAddPaymentInfo,
  trackInitiateCheckout,
  trackPurchase,
}
